<template>
  <div class="live-page">
    <van-sticky>
      <!-- 直播/视频播放器 -->
      <customize-video 
        :player-config="playerConfig" 
        :video-height="videoHeight"
        :match="match"
        :game="game"
        @layout-fixed="onPlayerLayoutFixed"
      />
      
      <!-- 队伍信息 -->
      <div class="team-info">
        <div class="team" v-if="game.type === 'game'">
          <div class="name">{{ teamInfo.homeTeamName }}</div>
          <div class="score">{{ teamInfo.homeScore }}:{{ teamInfo.awayScore }}</div>
          <div class="name">{{ teamInfo.awayTeamName }}</div>
        </div>
        <div v-else class="name">{{ game.name }}</div>
        <div class="match-name" @click="goMatchDetail">
          {{ teamInfo.matchName }}
          <van-icon name="arrow" />
        </div>
      </div>
    </van-sticky>
    <!-- 集锦&评论 -->
    <Container 
      :cols="cols" 
      :lives="lives" 
      :active-video-id="activeVideoId"
      @videoUrl="handleVideoUrl" 
      :commentList="commentList"
      :video-height="videoHeight"
      :interaction-allowed="interactionEnabled"
      :comment-allowed="commentInteractionAllowed"
      @interaction-not-allowed="showInteractionDisabledDialog"
      @comment-not-allowed="showCommentDisabledDialog"
    />
    <!-- 底部按钮区域 -->
    <div class="footer-btn">
      <div class="footer-box">
        <div class="btn-item" @click="likePost">
          <i class="iconfont icon-icon-dianzan1" :class="{'active': playerConfig?.alreadyLike}"></i>
          <span class="count">{{ interactiveStats.likeCount }}</span>
        </div>
        <div class="btn-item" @click="openCommentPopup">
          <i class="iconfont icon-icon-pinglun1"></i>
          <span class="count">{{ commentList.length }}</span>
        </div>
        <div v-if="playerConfig && playerConfig.objType !== 'bm_game_live'" class="btn-item" @click="handleDownload">
          <i class="iconfont icon-icon-xiazai1"></i>
          <span class="count">{{ interactiveStats.downloadCount }}</span>
        </div>
        <div class="btn-item" @click="handleShare">
          <i class="iconfont icon-icon-zhuanfa1"></i>
          <span class="count">{{ interactiveStats.shareCount }}</span>
        </div>
      </div>
    </div>
    <!-- 评论输入弹层 -->
    <van-popup
      v-model="commentPopupVisible"
      position="bottom"
      :style="{ height: 'auto' }"
      get-container="body"
      @opened="focusCommentInput"
    >
      <div class="comment-popup">
        <div class="comment-popup-bar">
          <span class="comment-cancel" @click="closeCommentPopup">{{ $t('livePage.commentCancel') }}</span>
          <span class="comment-title">{{ $t('livePage.commentPopupTitle') }}</span>
          <span class="comment-submit" @click="submitComment">{{ $t('livePage.commentSubmit') }}</span>
        </div>
        <input
          ref="commentInputRef"
          v-model="commentInput"
          type="text"
          class="comment-input"
          :placeholder="$t('livePage.commentPlaceholder')"
          maxlength="200"
          @keyup.enter="submitComment"
        />
      </div>
    </van-popup>
  </div>
</template>

<script>
import CustomizeVideo from './components/CustomizeVideo.vue'
import Container from './components/Container.vue'
import { GetBMGameLiveDetail, GetBMGameInteractiveStats, GetCommentList, AddComment, likePost, OnVideoDownload, OnVideoShare, bkGetGameDetail,viewVideoDetail } from '@/api/match'
import { getToken } from '@/libs/cookie-storage'
import { QNImageUtil, isWeChatMiniApp } from '@/utils'
import { AppType } from '@/constants/appType'
import wx from 'weixin-js-sdk'
import { buildFullHashRedirectUrl } from '@/utils/redirect'
import Moment from 'moment'
import Vue from 'vue'
import { Sticky, Popup, Toast, Dialog } from 'vant'
Vue.use(Sticky).use(Popup).use(Dialog)
export default {
  components: {
    CustomizeVideo,
    Container
  },
  data() {
    return {
      gameId: '',
      playerConfig: null,
      videoHeight: 0, // 视频高度，根据宽度和16:9比例计算
      appWidth: 0, // 容器宽度
      lastStatus: null, // 上一次的直播状态（用于判断是否需要重新构建配置）
      lastVideoUrl: null, // 上一次的视频URL（用于判断是否需要重新构建配置）
      pollTimer: null, // 定时器ID
      teamInfo: {}, // 队伍信息
      match: {}, // 赛事信息（含 poster）
      game: {}, // 比赛信息（含 beginDate）
      cols: [], // 集锦列表数据
      lives: [], // 打点列表数据
      commentList: [], // 评论列表数据
      isLogin: false, // 是否登录
      commentPopupVisible: false, // 评论弹层显隐
      commentInput: '', // 评论输入内容
      activeVideoId: '', // 当前选中的视频 id，用于 Container 高亮及分享
      hasAppliedVideoIdFromQuery: false, // 是否已按 URL videoId 做过首次定位，避免轮询时重复定位
      interactiveStats: { likeCount: 0, shareCount: 0, downloadCount: 0 }, // 互动统计：点赞、分享、下载
      /** bkGetGameDetail：interactEnabled=1 时允许点赞；评论还需 commentEnabled=1 */
      interactionEnabled: true,
      commentInteractionAllowed: true
    };
  },
  mounted() {
    this.gameId = this.$route.params.gameId
    this.fetchBkGameDetail()
    // 计算视频高度（1280 * 720 = 16:9 比例）
    this.appWidth = document.getElementById('app')?.clientWidth || window.innerWidth
    this.videoHeight = this.appWidth * 9 / 16 // 16:9 比例
    // 首次调用接口（该接口包含所有需要的数据）
    this.getBMGameLiveDetail()
    // 启动定时轮询（每5秒调用一次）
    this.startPolling()
    this.getCommentList()
    // 监听窗口大小变化，重新计算视频高度
    window.addEventListener('resize', this.handleResize)
    this.isLogin = getToken() ? true : false
  },
  beforeDestroy() {
    // 清理定时器
    this.stopPolling()
    // 清理事件监听器
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    goMatchDetail() {
      // this.$router.push({ path: `/event/activity?gameId=${this.gameId}` })
      this.$router.push({ path: `/new-game-detail/${this.gameId}/home` })
    },
    /**
     * 获取集锦列表数据（该接口包含所有需要的数据）
     */
    getBMGameLiveDetail() {
      const params = {
        gameId: this.gameId
      }
      GetBMGameLiveDetail(params).then(response => {
        if (response) {
          const data = response.data
          
          // 1. 处理集锦列表数据（cols）
          this.setColsData(data)
          
          // 2. 处理直播状态和播放器配置
          const liveStatus = data.liveInfo && data.liveInfo.liveStatus
          if (liveStatus) {
            console.log('直播状态:', liveStatus)
            
            // 判断状态是否发生变化，只有变化时才重新构建配置
            if (this.shouldRebuildConfig(liveStatus, data)) {
              this.buildPlayerConfig(liveStatus, data)
            }
          }
          
          // 3. 处理队伍信息
          this.setTeamInfo(data)
          // 4. 保存 match、game（供未开始直播时占位与倒计时）
          this.match = data.match || {}
          this.game = data.game || {}
          // 5. 处理集锦列表数据（lives）
          this.setLivesData(data)
          // 6. 若有 videoId 参数，定位到对应条目并播放
          this.applyVideoIdFromQuery()
          // 7. 获取互动统计（点赞、分享、下载）
          this.getInteractiveStats()
          // 8. BK 场次详情：是否允许评论（与轮询同步）
          this.fetchBkGameDetail()
        }
      }).catch(error => {
        console.error('获取数据失败:', error)
      })
    },
    /** BK 场次详情：interactEnabled 控制点赞+评论总开关；为 1 时再按 commentEnabled 放开评论 */
    fetchBkGameDetail() {
      bkGetGameDetail({ gameId: this.gameId }).then((res) => {
        if (res && res.data && res.data.game) {
          const g = res.data.game
          const interactOn = Number(g.interactEnabled) === 1
          this.interactionEnabled = interactOn
          this.commentInteractionAllowed =
            interactOn && Number(g.commentEnabled) === 1
        }
      }).catch(() => {})
    },
    showInteractionDisabledDialog() {
      Dialog.alert({
        message: `${this.$t('livePage.interactionDisabledLine1')}\n${this.$t('livePage.interactionDisabledLine2')}`,
        confirmButtonText: this.$t('livePage.dialogConfirm')
      })
    },
    showCommentDisabledDialog() {
      Dialog.alert({
        message: `${this.$t('livePage.commentDisabledLine1')}\n${this.$t('livePage.commentDisabledLine2')}`,
        confirmButtonText: this.$t('livePage.dialogConfirm')
      })
    },
    /** 获取互动统计：likeCount、shareCount、downloadCount */
    getInteractiveStats() {
      GetBMGameInteractiveStats({ gameId: this.gameId }).then((res) => {
        if (res && res.data) {
          this.interactiveStats = {
            likeCount: res.data.likeCount ?? 0,
            shareCount: res.data.shareCount ?? 0,
            downloadCount: res.data.downloadCount ?? 0
          }
        }
      }).catch(() => {})
    },
    getCommentList() {
      const params = {
        objId: this.gameId,
        objType: 'game',
        pageSize: 999,
      }
      GetCommentList(params).then(response => {
        const data = response.data
        this.commentList = data.commentList.map(item => {
          return {
            userName: item.author.nickName,
            userIcon: QNImageUtil.imageView(item.author.avatar, 28, 28),
            commentText: item.contentTxt,
            commentTime: Moment(item.createTime).format('M月DD日'),
            id: item.id,
          }
        }) || []
      })
    },
    /** 打开评论弹层：先校验登录，再显示输入框并聚焦 */
    openCommentPopup() {
      if (!this.interactionEnabled) {
        this.showInteractionDisabledDialog()
        return
      }
      if (!this.commentInteractionAllowed) {
        this.showCommentDisabledDialog()
        return
      }
      if (!this.loginType()) return
      this.commentInput = ''
      this.commentPopupVisible = true
    },
    /** 关闭评论弹层并清空输入 */
    closeCommentPopup() {
      this.commentPopupVisible = false
      this.commentInput = ''
    },
    /** 弹层打开后自动聚焦输入框 */
    focusCommentInput() {
      this.$nextTick(() => {
        setTimeout(() => { this.$refs.commentInputRef?.focus() }, 80)
      })
    },
    /** 提交评论：调用 AddComment，成功后刷新列表并关闭弹层 */
    submitComment() {
      const txt = (this.commentInput || '').trim()
      if (!txt) {
        Toast(this.$t('livePage.commentEmpty'))
        return
      }
      AddComment({
        objId: this.gameId,
        objType: 'game',
        contentTxt: txt
      }).then((res) => {
        if (res) {
          this.closeCommentPopup()
          this.getCommentList()
          Toast.success(this.$t('livePage.commentSendOk'))
        }
      })
    },
    /**
     * 设置集锦列表数据（cols）
     * @param {Object} data - 接口返回的数据对象
     */
    setColsData(data) {
      // 1. 首先赋值接口返回的 cols 数据
      this.cols = data.cols || []
      
      // 2. 检查是否有直播状态，如果有则添加到 cols 开头（参考 videoList.vue 268-282行）
      if (data.liveInfo && data.liveInfo.liveStatus) {
        const liveStatus = data.liveInfo.liveStatus
        const temp = {}
        temp.title = liveStatus.statusLabel
        temp.poster = liveStatus.poster
        temp.objType = 'bm_game_live'
        // temp.alreadyLike = data.whole.alreadyLike
        
        // 3. 如果有视频流，添加视频相关信息
        if (liveStatus.streams) {
          // 注意：原始代码中使用 liveStatus.streams.videoId（可能是 streams 对象有 videoId 属性）
          // 但 streamType 使用 liveStatus.streams[0].streamType（streams 是数组）
          temp.videoId = liveStatus.streams.videoId || (liveStatus.streams[0] && liveStatus.streams[0].videoId)
          temp.streamType = liveStatus.streams[0].streamType
          
          // 4. 根据状态选择 URL
          if (liveStatus.status === 1) {
            // 直播中：使用 HLS
            temp.url = liveStatus.streams[0].streamUrlHls
          } else {
            // 已结束：使用 MP4
            temp.url = liveStatus.streams[0].streamUrlMp4
          }
          if (liveStatus.status === 2 && data.whole) {
            temp.alreadyLike = data?.whole?.alreadyLike || 0
            temp.videoId = data?.whole?.videoId || ''
            temp.objType = 'bm_game'
            temp.isWholeReplay = true // 全场回放（视频录像），分享时不携带 videoId
          } else if (liveStatus.status === 1) {
            temp.objType = 'bm_game_live'
            temp.videoId = `bm_game_live_${this.gameId}`
          }
          // 5. 将直播状态项添加到 cols 数组的开头
          this.cols.unshift(temp)
        }
      }
      
      console.log('集锦列表数据:', this.cols)
    },
    /**
     * 设置打点列表数据（lives）
     * @param {Object} data - 接口返回的数据对象
     */
    setLivesData(data) {
      // 处理 lives 数据，格式化 URL 和封面图（参考 videoList.vue 292-296行）
      const lives = (data.lives || []).map(item => {
        return {
          ...item,
          poster: item.poster ? QNImageUtil.imageView(item.poster, 254, 144) : item.poster,
          url: item.url
        }
      })
      this.lives = lives
    },
    /**
     * 设置队伍信息
     * @param {Object} data - 接口返回的数据对象
     */
    setTeamInfo(data) {
      this.teamInfo = {
        homeTeamName: data.game.homeTeamName,
        homeScore: data.game.homeScore === -1 ? 0 : data.game.homeScore,
        awayTeamName: data.game.awayTeamName,
        awayScore: data.game.awayScore === -1 ? 0 : data.game.awayScore,
        matchName: data.match.chineseName,
      }
    },
    /**
     * 判断是否需要重新构建播放器配置
     * @param {Object} liveStatus - 当前直播状态
     * @param {Object} data - 完整数据对象
     * @returns {Boolean} 是否需要重新构建
     */
    shouldRebuildConfig(liveStatus) {
      // 如果没有直播状态或视频流，不构建配置
      if (!liveStatus || !liveStatus.streams || liveStatus.streams.length === 0) {
        // 如果之前有配置，现在没有了，需要更新
        if (this.playerConfig !== null) {
          this.lastStatus = null
          this.lastVideoUrl = null
          return true
        }
        return false
      }

      const videoStream = liveStatus.streams[0]
      const currentStatus = liveStatus.status
      
      // 确定当前视频URL
      let currentUrl = ''
      if (currentStatus === 1) {
        // 直播中：使用 HLS
        currentUrl = videoStream.streamUrlHls
      } else if (currentStatus === 2) {
        // 已结束有回放：优先使用 MP4，没有则用 HLS
        currentUrl = videoStream.streamUrlMp4 || videoStream.streamUrlHls
      }

      // 首次加载或状态/URL发生变化时，需要重新构建
      if (this.lastStatus === null || 
          this.lastStatus !== currentStatus || 
          this.lastVideoUrl !== currentUrl) {
        // 保存旧值用于日志
        const oldStatus = this.lastStatus
        const oldUrl = this.lastVideoUrl
        // 更新保存的状态
        this.lastStatus = currentStatus
        this.lastVideoUrl = currentUrl
        console.log('状态发生变化，重新构建播放器配置:', {
          oldStatus: oldStatus,
          newStatus: currentStatus,
          oldUrl: oldUrl,
          newUrl: currentUrl
        })
        return true
      }

      // 状态和URL都没有变化，不需要重新构建
      return false
    },
    /**
     * 根据 liveStatus.status 构建播放器配置对象
     * @param {Object} liveStatus - 直播状态对象
     * @param {Object} data - 完整数据对象
     */
    buildPlayerConfig(liveStatus, data) {
      // 如果没有直播状态或视频流，不构建配置
      if (!liveStatus || !liveStatus.streams || liveStatus.streams.length === 0) {
        this.playerConfig = null
        return
      }

      const videoStream = liveStatus.streams[0]
      const status = liveStatus.status
      
      // 根据状态确定视频类型和URL
      let type = '' // 'live': 直播中 hls, 'hls': 回放 hls, 'mp4': 回放 mp4
      let url = ''
      
      if (status === 1) {
        // 直播中：使用 HLS
        type = 'live'
        url = videoStream.streamUrlHls
      } else if (status === 2) {
        // 已结束有回放：优先使用 MP4，没有则用 HLS
        if (videoStream.streamUrlMp4) {
          type = 'mp4'
          url = videoStream.streamUrlMp4
        } else if (videoStream.streamUrlHls) {
          type = 'hls'
          url = videoStream.streamUrlHls
        }
      } else {
        // 其他状态（未开始/已结束无回放）：不构建配置
        this.playerConfig = null
        return
      }

      // 如果没有有效的URL，不构建配置
      if (!url) {
        this.playerConfig = null
        return
      }

      // 处理封面图（优先顺序：branding.img > videoStream.poster > match.poster）
      const branding = data.branding || {}
      const match = data.match || {}
      const poster = (branding.img) || (videoStream.poster) || (match.poster) || ''
      let videoId = videoStream.videoId || `bm_game_live_${this.gameId}`
      if (data.whole && data.whole.videoId) {
        videoId = data.whole.videoId
      }
      let objType = 'bm_game'
      let alreadyLike = 0
      if (status === 1) {
        objType = 'bm_game_live'
      } else if (status === 2 && data.whole) { 
        alreadyLike = data.whole.alreadyLike
      }
      // 构建播放器配置对象（参考 game1/index.vue 555-577行）
      const playerConfig = {
        id: 'myvideo',
        url: url,
        videoId: videoId,
        objType: objType,
        alreadyLike: alreadyLike,
        playsinline: true, // 开启ios和微信的内联模式
        autoplay: true, // 自动播放
        fluid: true, // 父元素宽高
        isLive: type === 'live', // 是否为直播
        crossOrigin: 'anonymous', // 处理CORS
        volume: 0.6,
        poster: poster ? QNImageUtil.imageView(poster, 1080, 608) : '', // 处理封面图，2倍尺寸适配高分辨率
        // 直播和点播的不同配置
        ...(type === 'live' ? {
          plugins: [window.HlsPlayer], // 使用HLS插件
          minCachedTime: 1, // 直播最小缓存时间
          maxCachedTime: 5, // 直播最大缓存时间
          playbackRate: '' // 直播不支持倍速
        } : {
          minCachedTime: 5, // 点播最小缓存时间
          maxCachedTime: 30, // 点播最大缓存时间
          playbackRate: [0.5, 0.75, 1, 1.5, 2] // 点播支持倍速
        })
      }

      // 非直播流不需要 HLS 插件
      if (type !== 'live') {
        // 如果配置中有 plugins，删除它（因为非直播不需要）
        // 注意：这里不设置 plugins，因为只有直播才需要
      }

      this.playerConfig = playerConfig
      this.activeVideoId = playerConfig.videoId || ''
      console.log('播放器配置对象:', playerConfig)
    },
    /**
     * 根据 URL 中的 videoId 参数，在 cols/lives 中查找并自动选中、播放
     * 仅进入页面首次执行，轮询后不再根据 videoId 重新定位、重播
     */
    applyVideoIdFromQuery() {
      if (this.hasAppliedVideoIdFromQuery) return
      const videoId = (this.$route.query && this.$route.query.videoId) || ''
      if (!videoId) return

      this.hasAppliedVideoIdFromQuery = true
      const idMatch = (item) => String(item.videoId || item.id || '') === String(videoId)
      const col = (this.cols || []).find(idMatch)
      const live = (this.lives || []).find(idMatch)
      const item = col || live
      if (!item || !item.url) return

      const videoData = {
        videoUrl: item.url,
        videoId: item.videoId || item.id,
        poster: item.poster,
        streamType: item.streamType || 'mp4',
        title: item.title,
        alreadyLike: item.alreadyLike,
        objType: item.objType || 'bm_game'
      }
      this.buildPlayerConfigFromVideoData(videoData)
    },
    /**
     * 在 URL 的 hash 查询中设置 videoId
     */
    setVideoIdInUrl(href, videoId) {
      const i = href.indexOf('#')
      if (i === -1) return href
      const base = href.substring(0, i + 1)
      const hash = href.substring(i + 1)
      const qi = hash.indexOf('?')
      let path = hash
      const query = {}
      if (qi >= 0) {
        path = hash.substring(0, qi)
        hash.substring(qi + 1).split('&').forEach((p) => {
          const [k, v] = p.split('=')
          if (k && v != null) query[decodeURIComponent(k)] = decodeURIComponent(v)
        })
      }
      query.videoId = videoId
      const qs = Object.keys(query).map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(query[k])).join('&')
      return base + path + (qs ? '?' + qs : '')
    },
    /**
     * 从 URL 的 hash 查询中移除 videoId
     */
    removeVideoIdFromUrl(href) {
      const i = href.indexOf('#')
      if (i === -1) return href
      const base = href.substring(0, i + 1)
      const hash = href.substring(i + 1)
      const qi = hash.indexOf('?')
      if (qi < 0) return href
      const path = hash.substring(0, qi)
      const query = {}
      hash.substring(qi + 1).split('&').forEach((p) => {
        const [k, v] = p.split('=')
        if (k && v != null && k !== 'videoId') query[decodeURIComponent(k)] = decodeURIComponent(v)
      })
      const qs = Object.keys(query).length ? Object.keys(query).map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(query[k])).join('&') : ''
      return base + path + (qs ? '?' + qs : '')
    },
    /**
     * 启动定时轮询
     */
    startPolling() {
      // 清除已存在的定时器
      this.stopPolling()
      // 每5秒调用一次接口（可根据需要调整为3秒）
      this.pollTimer = setInterval(() => {
        this.getBMGameLiveDetail()
      }, 5000)
    },
    /**
     * 停止定时轮询
     */
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    /**
     * 处理视频URL变化（来自Container组件的点击事件，切换集锦或打点时触发）
     * @param {Object} videoData - 视频数据对象 { videoUrl, videoId, poster, streamType, title }
     */
    handleVideoUrl(videoData) {
      if (!videoData || !videoData.videoUrl) {
        console.warn('视频数据无效:', videoData)
        return
      }

      // 根据视频数据构造播放器配置
      this.buildPlayerConfigFromVideoData(videoData)

      // 切换集锦/打点时上报浏览量
      const videoId = this.playerConfig && this.playerConfig.videoId
      if (videoId) {
        viewVideoDetail({ id: videoId }).catch(() => {})
      }
    },
    /**
     * 根据视频数据构建播放器配置对象
     * @param {Object} videoData - 视频数据对象 { videoUrl, videoId, poster, streamType, title }
     */
    buildPlayerConfigFromVideoData(videoData) {
      const { videoUrl, poster, streamType } = videoData
      
      // 确定视频类型
      let type = 'mp4' // 默认类型
      if (streamType === 'live') {
        type = 'live'
      } else if (videoUrl && videoUrl.indexOf('.m3u8') > -1) {
        type = 'hls'
      } else if (videoUrl && videoUrl.indexOf('.mp4') > -1) {
        type = 'mp4'
      }

      // 处理封面图
      const posterUrl = poster ? QNImageUtil.imageView(poster, 1080, 608) : ''
      const objType = videoData.objType || 'bm_game'
      // 构建播放器配置对象
      const playerConfig = {
        id: 'myvideo',
        videoId: videoData.videoId,
        alreadyLike: videoData.alreadyLike,
        url: videoUrl,
        objType: objType,
        playsinline: true, // 开启ios和微信的内联模式
        autoplay: true, // 自动播放
        fluid: true, // 父元素宽高
        isLive: type === 'live', // 是否为直播
        crossOrigin: 'anonymous', // 处理CORS
        volume: 0.6,
        poster: posterUrl, // 封面图
        // 直播和点播的不同配置
        ...(type === 'live' ? {
          plugins: [window.HlsPlayer], // 使用HLS插件
          minCachedTime: 1, // 直播最小缓存时间
          maxCachedTime: 5, // 直播最大缓存时间
          playbackRate: '' // 直播不支持倍速
        } : {
          minCachedTime: 5, // 点播最小缓存时间
          maxCachedTime: 30, // 点播最大缓存时间
          playbackRate: [0.5, 0.75, 1, 1.5, 2] // 点播支持倍速
        })
      }

      // 更新播放器配置
      this.playerConfig = playerConfig
      this.activeVideoId = videoData.videoId || ''
      console.log('根据点击项构建播放器配置:', playerConfig)
    },
    /**
     * 处理窗口大小变化
     */
    handleResize() {
      this.appWidth = document.getElementById('app')?.clientWidth || window.innerWidth
      this.videoHeight = this.appWidth * 9 / 16 // 16:9 比例
    },
    /** 全屏退出后 xgplayer 会修正尺寸，父级同步重算 videoHeight，避免 Tab sticky 偏移错位 */
    onPlayerLayoutFixed() {
      this.handleResize()
    },
    likePost() {
      console.log(this.playerConfig,'this.playerConfig');
      if (!this.interactionEnabled) {
        this.showInteractionDisabledDialog()
        return
      }
      if(!this.loginType()) return
      const params = {
        objType: this.playerConfig.objType,
        contentType: 'video',
        targetId: this.playerConfig.videoId,
        objId: this.gameId
      }
      likePost(params).then(res => {
        if (res) {
          this.playerConfig.alreadyLike = 1
          this.getInteractiveStats()
        }
      })
    },
    /**
     * 下载：区分 H5 与 微信小程序
     * - H5：复制链接到剪贴板
     * - 微信小程序：postMessage 的 bindmessage 只在 后退/分享/复制链接/销毁 时触发，点击下载不会触发这些时机，
     *   故改为 navigateTo 跳转到小程序的下载页，在 query 中带 url，由该页 onLoad 时处理下载，实现点击即生效。
     *   小程序需实现 /pages/download/index，接收 options.url 并调 wx.downloadFile 等；若路径不同，请修改下方 path。
     */
    handleDownload() {
      const id = this.buildVideoRecordId()
      if (id) {
        OnVideoDownload({ id: id }).then(() => this.getInteractiveStats()).catch(() => {})
      }
      const url = this.getDownloadUrl()
      isWeChatMiniApp().then((isMini) => {
        if (isMini) {
          // const mp = window.wx && window.wx.miniProgram
          // if (mp && typeof mp.navigateTo === 'function') {
            const path = `/pages/download/index?url=${encodeURIComponent(url)}`
            wx.miniProgram.navigateTo({ url: path })
          // }
          // this.copyToClipboard(url).then(() => Toast('链接已复制')).catch(() => Toast('链接已复制'))
        } else {
          this.copyToClipboard(url).then(() => Toast('链接已复制')).catch(() => Toast('复制失败，请手动复制链接'))
        }
      })
    },
    /**
     * 分享：区分 小程序 / H5
     * - 小程序：postMessage 发送 setShareData，用户点击右上角「分享」时 bindmessage 会收到并用于生成分享卡片
     * - H5 微信内 / App 内：this.wxShare（走 JSSDK 或 ds-bridge）
     * - H5 纯浏览器：复制链接
     */
    handleShare() {
      const id = this.buildVideoRecordId()
      if (id) {
        OnVideoShare({ id: id }).then(() => this.getInteractiveStats()).catch(() => {})
      }
      const shareParams = this.buildShareParams()
      isWeChatMiniApp().then((isMini) => {
        if (isMini) {
            wx.miniProgram.postMessage({
              data: {
                type: 'setShareData',
                shareData: {
                  title: shareParams.title,
                  desc: shareParams.abstract,
                  imageUrl: shareParams.image,
                  path: '/pages/webview/index?redirect=' + encodeURIComponent(shareParams.link)
                }
              }
            })
          Toast('请点击右上角···进行分享')
        } else if (AppType.isInWeChat() || AppType.isInApp()) {
          this.wxShare(shareParams)
        } else {
          this.copyToClipboard(shareParams.link).then(() => Toast('链接已复制，快去分享吧')).catch(() => Toast('链接已复制，快去分享吧'))
        }
      })
    },
    /** 构建分享参数 { title, abstract, link, image }：标题=game.name 或 VS，副标题=当前条目 title，封面=视频封面，link 按规则携带/移除 videoId */
    buildShareParams() {
      const t = this.teamInfo || {}
      const title = (this.game && this.game.name) || (t.homeTeamName && t.awayTeamName ? `${t.homeTeamName} VS ${t.awayTeamName}` : (this.match && this.match.chineseName) || '精彩赛事')

      const curId = this.playerConfig && this.playerConfig.videoId
      const idMatch = (item) => String(item.videoId || item.id || '') === String(curId)
      const currentItem = (this.cols || []).find(idMatch) || (this.lives || []).find(idMatch)
      const abstract = (currentItem && currentItem.title) || '精彩直播，点击观看'

      const isWholeReplay = !!(this.cols && this.cols[0] && this.cols[0].isWholeReplay && this.cols[0].objType === 'bm_game' && curId && String(this.cols[0].videoId || this.cols[0].id || '') === String(curId))
      const shouldCarryVideoId = !!(this.playerConfig && !this.playerConfig.isLive && !isWholeReplay && curId)
      const link = shouldCarryVideoId ? this.setVideoIdInUrl(window.location.href, curId) : this.removeVideoIdFromUrl(window.location.href)

      const image = (this.playerConfig && this.playerConfig.poster) || (currentItem && currentItem.poster) || ((this.match && (this.match.poster || this.match.icon)) ? QNImageUtil.imageView(this.match.poster || this.match.icon, 300, 300) : '')

      return { title, abstract, link, image }
    },
    /** 构建记录下载/分享的 Id：videoId&objType&objId，其中 objId=gameId；videoId、objType 来自 playerConfig（对应 cols/lives） */
    buildVideoRecordId() {
      if (!this.gameId) return ''
      const v = this.playerConfig?.videoId ?? ''
      const o = this.playerConfig?.objType ?? ''
      return `${v}&${o}&${this.gameId}`
    },
    /** 下载用链接：有 mp4 直链则返回，否则返回当前页 */
    getDownloadUrl() {
      const u = this.playerConfig && this.playerConfig.url
      if (u && String(u).indexOf('.mp4') > -1) return u
      return window.location.href
    },
    /** 复制文本到剪贴板 */
    copyToClipboard(text) {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        return navigator.clipboard.writeText(text)
      }
      return new Promise((resolve, reject) => {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.left = '-9999px'
        document.body.appendChild(ta)
        ta.select()
        try {
          document.execCommand('copy')
          resolve()
        } catch (e) {
          reject(e)
        } finally {
          document.body.removeChild(ta)
        }
      })
    },
    /**
     * 判断是否登录；未登录时按环境跳转：H5 → /auth/login，微信小程序内 H5 → 小程序 /pages/common/login
     * @returns {boolean} 已登录 true，未登录并已跳转 false
     */
    loginType() {
      if (this.isLogin) return true
      const redirect = (window.location.hash || '#').substring(1) || this.$route.fullPath
      isWeChatMiniApp().then((isMini) => {
        if (isMini) {
          wx.miniProgram.navigateTo({
            url: redirect ? `/pages/common/login?redirect=${encodeURIComponent(buildFullHashRedirectUrl('/' + String(redirect).replace(/^#?\/?/, '')))}`
              : '/pages/common/login'
          })
        } else {
          this.$router.push({ path: '/auth/login', query: { redirect: encodeURIComponent(redirect) } })
        }
      })
      return false
    },
  },
};
</script>

<style scoped lang="scss">
.live-page {
  width: 100%;
  height: 100vh;
  box-sizing: border-box;
  background-color: #fff;
}
.team-info{
  padding: 13px 16px;
  height: 70px;
  box-sizing: border-box;
  background-color: #fff;
  .team{
    display: flex;
    align-items: center;
    .score{
      font-size: 14px;
      color: #000;
      margin: 0 10px;
    }
  }
     .name{
      font-size: 14px;
      color: #000;
    }
  .match-name{
    color: #7C7B7B;
    font-size: 12px;
    margin-top: 7px;
  }
}
.footer-btn{
  height: 88px;
  .footer-box{
    position: fixed;
    bottom: 0;
    width: 100%;
    background-color: #fff;
    display: flex;
    justify-content: space-around;
    align-items: center;
    padding-top: 12px;
    padding-bottom: 22px;
    box-sizing: border-box;
    box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.3);
    .btn-item{
      width: 47px;
      min-height: 30px;
      display: flex;
      align-items: center;
      gap: 2px;
      .iconfont{
        font-size: 24px;
      }
      .active{
        color: #FF4D4F;
      }
      .count{
        font-size: 13px;
        color: #000;
        line-height: 1;
        margin-left: 3px;
      }
    }
  }
}
/* 评论弹层 */
.comment-popup {
  padding: 12px 16px 24px;
  background: #fff;
  border-radius: 12px 12px 0 0;
}
.comment-popup-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  .comment-cancel {
    font-size: 15px;
    color: #969799;
  }
  .comment-title {
    font-size: 16px;
    font-weight: 500;
    color: #323233;
  }
  .comment-submit {
    font-size: 15px;
    color: #D54F3F;
  }
}
.comment-input {
  width: 100%;
  height: 44px;
  padding: 0 12px;
  font-size: 15px;
  border: 1px solid #ebedf0;
  border-radius: 8px;
  box-sizing: border-box;
  outline: none;
  &::placeholder {
    color: #c8c9cc;
  }
}
</style>
